import {
  AdminPanelSettings,
  BrokenImage,
  Dashboard,
  ExpandLess,
  ExpandMore,
  Group,
  LocationOn,
  SatelliteAlt,
} from '@mui/icons-material';
import {
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import GridOnIcon from '@mui/icons-material/GridOn';
import { Link } from 'react-router-dom';
import { useIsAdmin } from 'features/auth/hooks/useIsAdmin';
import { useIsViewer } from 'features/auth/hooks/useIsViewer';
import { useEffect, useState } from 'react';
import useInLightMode from 'hooks/useInLightMode';
import { useIsSuperUser } from 'features/auth/hooks/useIsSuperUser';
import { ReactComponent as DishIcon } from './satellite-dish-solid.svg';
import { ReactComponent as NetworkIcon } from './network-wired-solid.svg';
import { StyledDrawerList } from './StyledDrawerList';

interface Props {
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ConfigurationList = ({ drawerOpen, setDrawerOpen }: Props) => {
  const isAdmin = useIsAdmin();
  const isViewer = useIsViewer();
  const isSuperUser = useIsSuperUser();
  const inLightMode = useInLightMode();

  const [lptOpen, setLptOpen] = useState(false);
  const [systemOpen, setSystemOpen] = useState(false);

  const handleSystemClick = () => {
    setSystemOpen(!systemOpen);
    setDrawerOpen(true);
  };

  const handleLptClick = () => {
    setLptOpen(!lptOpen);
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (!drawerOpen) {
      setLptOpen(false);
      setSystemOpen(false);
    }
  }, [drawerOpen]);

  return (
    <StyledDrawerList sx={{ padding: 0 }}>
      <ListItemButton component={Link} to='/' key={'Dashboard'}>
        <ListItemIcon>
          <Dashboard />
        </ListItemIcon>
        <ListItemText primary={'Dashboard'} />
      </ListItemButton>
      {isAdmin && (
        <ListItemButton component={Link} to='/user-management'>
          <ListItemIcon>
            <Group />
          </ListItemIcon>
          {drawerOpen ? (
            <ListItemText primary='User Management' />
          ) : (
            <ListItemText primary='Link' />
          )}
        </ListItemButton>
      )}
      {isAdmin && (
        <ListItemButton component={Link} to='/satellites'>
          <ListItemIcon>
            <SatelliteAlt />
          </ListItemIcon>
          <ListItemText primary='Satellites' />
        </ListItemButton>
      )}
      {isAdmin && (
        <ListItemButton component={Link} to='/dishes'>
          <ListItemIcon>
            <DishIcon
              height='20'
              style={{
                filter: inLightMode
                  ? 'invert(47%) sepia(0%) saturate(320%) hue-rotate(223deg) brightness(95%) contrast(84%)'
                  : 'invert(84%) sepia(7%) saturate(169%) hue-rotate(169deg) brightness(109%) contrast(84%)',
              }}
            />
          </ListItemIcon>
          <ListItemText primary='Dishes' />
        </ListItemButton>
      )}
      {isAdmin && (
        <ListItemButton component={Link} to='/instruments'>
          <ListItemIcon>
            <NetworkIcon
              height='20'
              style={{
                filter: inLightMode
                  ? 'invert(47%) sepia(0%) saturate(320%) hue-rotate(223deg) brightness(95%) contrast(84%)'
                  : 'invert(84%) sepia(7%) saturate(169%) hue-rotate(169deg) brightness(109%) contrast(84%)',
              }}
            />
          </ListItemIcon>
          <ListItemText primary='Instruments' />
        </ListItemButton>
      )}
      {isAdmin && (
        <ListItemButton component={Link} to='/locations'>
          <ListItemIcon>
            <LocationOn />
          </ListItemIcon>
          <ListItemText primary='Locations' />
        </ListItemButton>
      )}
      <ListItemButton component={Link} to='/snapshots'>
        <ListItemIcon>
          <BrokenImage />
        </ListItemIcon>
        <ListItemText primary='Snapshots' />
      </ListItemButton>
      <ListItemButton component={Link} to='/walls'>
        <ListItemIcon>
          <GridOnIcon />
        </ListItemIcon>
        <ListItemText primary='Walls' />
      </ListItemButton>
      {!isViewer && (
        <>
          <ListItemButton onClick={handleSystemClick}>
            <ListItemIcon>
              <BuildIcon />
            </ListItemIcon>
            <ListItemText primary='System' />
            {systemOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={systemOpen} timeout='auto' unmountOnExit>
            <StyledDrawerList disablePadding>
              {isAdmin && (
                <ListItemButton component={Link} to='/system-overview'>
                  <ListItemText primary='Overview' />
                </ListItemButton>
              )}
              <ListItemButton component={Link} to='/console'>
                <ListItemText primary='Console' />
              </ListItemButton>
              {isAdmin && (
                <ListItemButton component={Link} to='/external-link-setup'>
                  <ListItemText primary='External Links' />
                </ListItemButton>
              )}
              <ListItemButton component={Link} to='/system-info'>
                <ListItemText primary='System Info' />
              </ListItemButton>
            </StyledDrawerList>
          </Collapse>
        </>
      )}
      {isSuperUser && process.env.REACT_APP_MODE === 'LPT' && (
        <>
          <ListItemButton onClick={handleLptClick}>
            <ListItemIcon>
              <AdminPanelSettings />
            </ListItemIcon>
            <ListItemText primary='LPT' />
            {lptOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={lptOpen} timeout='auto' unmountOnExit>
            <StyledDrawerList disablePadding>
              <ListItemButton component={Link} to='/create-licenses'>
                <ListItemText primary='Create Licenses' />
              </ListItemButton>
            </StyledDrawerList>
            <StyledDrawerList disablePadding>
              <ListItemButton component={Link} to='/create-server-licenses'>
                <ListItemText primary='Create Server Licenses' />
              </ListItemButton>
            </StyledDrawerList>
          </Collapse>
        </>
      )}
    </StyledDrawerList>
  );
};

export default ConfigurationList;
